import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { PanelGroup, Panel, PanelResizeHandle } from 'react-resizable-panels';
import { useShallow } from 'zustand/react/shallow';
// mantine
import { Divider } from '@mantine/core';
// components
import Skills from '@/modules/Designer/components/Skills';
import Parameters from '@/modules/Designer/components/Parameters';
import SkillSettings from '@/modules/Designer/components/Skills/components/SkillSettings';
import SidePanel from '@/modules/Designer/components/SidePanel';
import ScriptEditor from '@/pages/Library/components/ScriptEditor';
import LibraryHeader from '@/pages/Library/components/LibraryHeader';
// utils
import useLibraryStore from '@/pages/Library/Library.store';
import { useSkills, useSkillParameters } from '@/pages/Library/Library.hooks';

const Library = () => {
    const { libraryId } = useParams();
    const { selectedSkillId, setSelectedSkillId } = useLibraryStore(
        useShallow((state) => ({
            selectedSkillId: state.selectedSkillId,
            setSelectedSkillId: state.setSelectedSkillId
        }))
    );

    const {
        skills,
        onSkillCreate,
        onSkillDelete,
        onSkillUpdate,
        refetch: refetchSkills
    } = useSkills(libraryId);

    const selectedSkill = skills.find((skill) => skill.id === selectedSkillId);

    useEffect(() => {
        if (skills.length > 0 && !selectedSkillId) {
            setSelectedSkillId(skills[0].id);
        }
    }, [skills, selectedSkillId, setSelectedSkillId]);

    const { onParameterCreate, onParameterDelete, onParameterUpdate } =
        useSkillParameters(libraryId, selectedSkillId);

    const handleSelectSkill = (skillId: string) => {
        setSelectedSkillId(skillId);
    };

    return (
        <div className="w-full h-full flex flex-col">
            <Helmet>
                <title>NEWO - Library</title>
            </Helmet>
            <LibraryHeader refetchSkills={refetchSkills} skills={skills} />
            <div className="flex-1 min-h-0">
                <PanelGroup direction="horizontal">
                    <SidePanel>
                        <Skills
                            items={skills}
                            selectedSkillId={selectedSkillId}
                            onCreate={onSkillCreate}
                            onDelete={onSkillDelete}
                            onSelect={handleSelectSkill}
                        />
                    </SidePanel>
                    <PanelResizeHandle className="w-0.5 bg-gray-4" />
                    <Panel className="pt-1" defaultSize={60} minSize={25}>
                        {selectedSkill ? (
                            <ScriptEditor skill={selectedSkill} />
                        ) : undefined}
                    </Panel>
                    <PanelResizeHandle className="w-0.5 bg-gray-4" />
                    <SidePanel>
                        {selectedSkill ? (
                            <SkillSettings
                                skill={selectedSkill}
                                onUpdate={onSkillUpdate}
                            />
                        ) : undefined}
                        <Divider />
                        {selectedSkill ? (
                            <Parameters
                                items={selectedSkill.parameters}
                                onCreate={onParameterCreate}
                                onDelete={onParameterDelete}
                                onUpdate={onParameterUpdate}
                            />
                        ) : undefined}
                    </SidePanel>
                </PanelGroup>
            </div>
        </div>
    );
};

export default Library;
